import { useEffect } from 'react';
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type FormState,
  type UseFormSetError,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { SelectField } from '@brighte/form-components';
import { EnterpriseStructure, isBusinessLoanProduct } from '@brighte/validation';
import { getEnumKey } from '@components/StepNavigator/helper';
import { Text } from '@spark-web/text';
import FieldArray from '../../components/FieldArray/FieldArray';
import { BENEFICIAL_OWNER_DETAILS, LABELS, OWNERSHIP_DETAILS, pascalToCamelCase } from '../../utils';
import BeneficialIdentification from './BeneficialIdentification';
import { generateOptionsFromMaxCount } from './helpers';

interface BeneficialOwnerProps<TFieldValues extends FieldValues = FieldValues> {
  name: string;
  watch: UseFormWatch<TFieldValues>;
  control: Control<TFieldValues>;
  businessType: EnterpriseStructure;
  setValue?: UseFormSetValue<TFieldValues>;
  formState: FormState<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
  defaultValues: Record<string, unknown>[];
  financialProductId?: string;
  clearErrors?: (name?: string | string[]) => void;
}

const initialValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  ownershipPercentage: 0,
};

function BeneficialOwner<TFieldValues extends FieldValues = FieldValues>({
  name,
  watch,
  control,
  businessType,
  setValue,
  formState,
  setError,
  defaultValues,
  financialProductId,
  clearErrors,
}: BeneficialOwnerProps<TFieldValues>) {
  const { NUMBER_OF_BENEFICIAL_OWNERS } = LABELS;
  const {
    BENEFICIAL_DESCRIPTION,
    BENEFICIAL_OWNER,
    TELL_ABOUT_BENEFICIAL,
    BENEFICIAL_IDENTIFICATION_DESCRIPTION,
    NUMBER_OF_BENEFICIAL_OWNER_OPTIONS,
  } = OWNERSHIP_DETAILS;

  const structureProp = pascalToCamelCase(getEnumKey(businessType, EnterpriseStructure));
  const beneficialDropdown = `${structureProp}.numOfBeneficialOwner` as FieldPath<TFieldValues>;
  const beneficialList = watch(name as FieldPath<TFieldValues>);
  const errors = formState?.errors;

  useEffect(() => {
    // sets the error for beneficial dropdown
    const errorField = errors?.enterprise?.[structureProp]?.beneficialOwners;
    if (errorField?.message) {
      setError(beneficialDropdown, errorField);
    }
  }, [errors, structureProp, beneficialDropdown, setError]);

  useEffect(() => {
    // pre-populate beneficial dropdown value
    if (beneficialList?.length > 0) {
      setValue?.(beneficialDropdown, beneficialList.length);
    }
  }, [beneficialList, beneficialDropdown, setValue]);

  return (
    <>
      <Text>{TELL_ABOUT_BENEFICIAL}</Text>
      <FieldArray name={name} control={control} defaultValues={initialValues}>
        {({ fields, handleChange }) => (
          <>
            <SelectField
              name={beneficialDropdown}
              control={control}
              label={NUMBER_OF_BENEFICIAL_OWNERS}
              fieldProps={{
                description: isBusinessLoanProduct(financialProductId)
                  ? BENEFICIAL_OWNER_DETAILS.BENEFICIAL_DESCRIPTION
                  : BENEFICIAL_DESCRIPTION,
              }}
              options={generateOptionsFromMaxCount(isBusinessLoanProduct(financialProductId) ? 10 : 4, 1)}
              onChange={handleChange}
              placeholder={NUMBER_OF_BENEFICIAL_OWNER_OPTIONS}
            />
            {fields.length > 0 ? (
              <>
                {!isBusinessLoanProduct(financialProductId) ? (
                  <Text>{BENEFICIAL_IDENTIFICATION_DESCRIPTION}</Text>
                ) : null}
                {fields.map((item, index) => (
                  <BeneficialIdentification
                    title={`${BENEFICIAL_OWNER} #${index + 1}`}
                    name={`${name}.${[index]}`}
                    key={item.id}
                    control={control}
                    watch={watch}
                    businessType={businessType}
                    setValue={setValue}
                    defaultValues={defaultValues[index] ?? {}}
                    financialProductId={financialProductId}
                    clearErrors={clearErrors}
                  />
                ))}
              </>
            ) : null}
          </>
        )}
      </FieldArray>
    </>
  );
}

export default BeneficialOwner;
